import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { AdminFeaturesService } from '../service/admin-features.service';

@Component({
  selector: 'app-news-admin',
  templateUrl: './news-admin.component.html',
  providers: [MessageService],
})
export class NewsAdminComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  rows: any[] = [];
  loading = false;
  modalOpen = false;
  editing: any = null;
  form: any = this.blank();
  slugTouched = false;

  constructor(private svc: AdminFeaturesService, private toast: MessageService) {}

  ngOnInit() { this.load(); }

  uploadingImage = false;
  uploadingFile = false;

  blank() {
    return { title: '', slug: '', preview: '', body: '', imageUrl: '', publishedAt: '',
             attachmentKey: '', attachmentName: '' };
  }

  /** Upload the post image; the returned URL is what the post stores. */
  uploadImage(ev: any) {
    const f = ev && ev.target && ev.target.files && ev.target.files[0];
    if (!f) { return; }
    this.uploadingImage = true;
    const fd = new FormData();
    fd.append('file', f, f.name);
    this.svc.uploadNewsImage(fd).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => { this.form.imageUrl = r.url; this.uploadingImage = false; },
      () => { this.uploadingImage = false; this.err('Image upload failed'); },
    );
  }

  /** Upload the downloadable attachment; the post keeps its storage key. */
  uploadFile(ev: any) {
    const f = ev && ev.target && ev.target.files && ev.target.files[0];
    if (!f) { return; }
    this.uploadingFile = true;
    const fd = new FormData();
    fd.append('file', f, f.name);
    this.svc.uploadNewsFile(fd).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => {
        this.form.attachmentKey = r.attachmentKey;
        this.form.attachmentName = r.attachmentName;
        this.uploadingFile = false;
      },
      () => { this.uploadingFile = false; this.err('Attachment upload failed'); },
    );
  }

  load() {
    this.loading = true;
    this.svc.listNews().pipe(takeUntil(this.destroy$)).subscribe((r: any[]) => {
      this.rows = r || []; this.loading = false;
    }, () => { this.loading = false; this.err('Failed to load news'); });
  }

  slugify(s: string) { return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }
  onTitle() { if (!this.slugTouched) this.form.slug = this.slugify(this.form.title); }

  openNew() { this.editing = null; this.form = this.blank(); this.slugTouched = false; this.modalOpen = true; }
  openEdit(row: any) {
    this.editing = row;
    this.form = {
      title: row.title, slug: row.slug, preview: row.preview || '', body: row.body || '',
      imageUrl: row.imageUrl || '', publishedAt: row.publishedAt ? row.publishedAt.substring(0, 10) : '',
      attachmentKey: row.attachmentKey || '', attachmentName: row.attachmentName || '',
    };
    this.slugTouched = true; this.modalOpen = true;
  }
  close() { this.modalOpen = false; }

  save(publish: boolean) {
    if (!this.form.title || !this.form.slug) { this.err('Title and slug are required'); return; }
    const body: any = { ...this.form };
    if (publish && !body.publishedAt) body.publishedAt = new Date().toISOString();
    if (!publish) body.publishedAt = '';   // save as draft
    const req = this.editing ? this.svc.updateNews(this.editing.id, body) : this.svc.saveNews(body);
    req.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.ok(this.editing ? 'Post updated' : 'Post created'); this.close(); this.load();
    }, () => this.err('Save failed'));
  }

  remove(row: any) {
    if (!confirm(`Delete "${row.title}"?`)) return;
    this.svc.deleteNews(row.id).pipe(takeUntil(this.destroy$)).subscribe(() => { this.ok('Deleted'); this.load(); }, () => this.err('Delete failed'));
  }

  isPublished(row: any) { return !!row.publishedAt && new Date(row.publishedAt) <= new Date(); }

  private ok(m: string)  { this.toast.add({ severity: 'success', summary: m }); }
  private err(m: string) { this.toast.add({ severity: 'error', summary: m }); }

  ngOnDestroy() { this.destroy$.next(); this.destroy$.complete(); }
}
